import React, { useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Target, Users, Coins, Newspaper, X } from "lucide-react";

const DISMISS_KEY = "stocker.pillarsIntro.dismissed";

/**
 * NEW component (2026-08-30, flow/psychology review of the dashboard --
 * see Technical_Docs/Architecture_Rebuild). Stocker has four differentiated
 * pillars (credibility-scored predictions, groups/community, credibility-
 * gated creator monetization, published news) and nothing on the dashboard
 * ever names them as a set -- a new user has to piece the product together
 * from individual widgets. This is a compact, dismissible strip naming all
 * four with a one-line reason each and a direct link, not a tour or modal.
 * Dismissal is remembered per-device via localStorage (a UI convenience,
 * not data that needs to persist server-side or sync across devices) so
 * returning users aren't shown this every visit.
 *
 * Pillar labels are this pass's own proposal, not an already-agreed,
 * written-down list -- the only prior reference anywhere in this codebase
 * is a single comment calling Published News "the 4th Pillar" without ever
 * naming 1-3. Worth confirming the exact framing/wording with the team
 * rather than treating this copy as final.
 */
const PillarsIntro = () => {
  const { t } = useTranslation();
  const [dismissed, setDismissed] = useState(() => {
    try {
      return localStorage.getItem(DISMISS_KEY) === "1";
    } catch {
      return false;
    }
  });

  const handleDismiss = useCallback(() => {
    setDismissed(true);
    try {
      localStorage.setItem(DISMISS_KEY, "1");
    } catch {
      // localStorage unavailable (private browsing, blocked storage) --
      // dismissal just won't persist across reloads, not worth failing over
    }
  }, []);

  if (dismissed) return null;

  const pillars = [
    {
      key: "predictions",
      to: "/leaderboard",
      icon: Target,
      accent: "text-violet-700 dark:text-violet-400",
      title: t("dashboard.pillarsIntro.predictions.title"),
      body: t("dashboard.pillarsIntro.predictions.body"),
    },
    {
      key: "groups",
      to: "/groupies",
      icon: Users,
      accent: "text-blue-700 dark:text-blue-400",
      title: t("dashboard.pillarsIntro.groups.title"),
      body: t("dashboard.pillarsIntro.groups.body"),
    },
    {
      key: "monetization",
      to: "/pricing",
      icon: Coins,
      accent: "text-emerald-700 dark:text-emerald-400",
      title: t("dashboard.pillarsIntro.monetization.title"),
      body: t("dashboard.pillarsIntro.monetization.body"),
    },
    {
      key: "news",
      to: "/news",
      icon: Newspaper,
      accent: "text-amber-700 dark:text-amber-400",
      title: t("dashboard.pillarsIntro.news.title"),
      body: t("dashboard.pillarsIntro.news.body"),
    },
  ];

  return (
    // Same fix as DashboardHero.jsx (2026-09-11): the gradient tint had no
    // light-mode counterpart and read as a washed lavender card on an
    // otherwise white/neutral dashboard. White card + violet border in
    // light mode; the tinted gradient stays dark-mode-only.
    <div className="relative mb-4 sm:mb-6 rounded-xl border border-violet-200 dark:border-violet-500/20 bg-white dark:bg-gradient-to-br dark:from-violet-500/10 dark:via-indigo-500/5 dark:to-blue-500/10 p-4 sm:p-5">
      <button
        type="button"
        onClick={handleDismiss}
        aria-label={t("dashboard.pillarsIntro.dismiss")}
        className="absolute top-3 right-3 p-1 rounded-lg text-gray-400 dark:text-slate-500 hover:text-gray-600 dark:hover:text-slate-300 hover:bg-cream-900/5 dark:hover:bg-slate-700/40 transition-colors"
      >
        <X className="w-4 h-4" />
      </button>

      <h2 className="text-sm font-bold text-gray-800 dark:text-slate-200 mb-1 pr-8">
        {t("dashboard.pillarsIntro.heading")}
      </h2>
      <p className="text-xs text-gray-500 dark:text-slate-400 mb-4 pr-8">
        {t("dashboard.pillarsIntro.subheading")}
      </p>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {pillars.map(({ key, to, icon: Icon, accent, title, body }) => (
          <Link
            key={key}
            to={to}
            className="flex flex-col gap-1.5 p-3 rounded-lg bg-cream-100 dark:bg-slate-800/50 border border-gray-200 dark:border-slate-700/50 hover:border-violet-400/40 dark:hover:border-violet-500/40 transition-colors"
          >
            <Icon className={`w-4 h-4 ${accent}`} />
            <span className="text-xs font-semibold text-gray-800 dark:text-slate-200">{title}</span>
            <span className="text-[11px] text-gray-500 dark:text-slate-400 leading-snug">{body}</span>
          </Link>
        ))}
      </div>
    </div>
  );
};

export default PillarsIntro;
